import { useCallback, useEffect, useMemo, useRef } from "react";
import useTourController from "./useTourController";
import { isTourSeen, markTourSeen } from "./tourSeen";
import useTourStore from "Store/useTourStore";
import useAuthStore from "Store/useAuthStore";
import { decodeToken } from "Components/SbSessionCheckerComponent/SbSessionChecker";

// Read the access token safely: localStorage can be absent (SSR) or throw when
// disabled (private mode / embedded webviews), so never let it crash the hook.
const readStoredToken = () => {
  try {
    return typeof localStorage !== "undefined" ? localStorage.getItem("access_token") : null;
  } catch {
    return null;
  }
};

/**
 * useModuleTour — shared wiring for a module's guided tour.
 *
 * Composes the reusable tour helpers so a module doesn't repeat the boilerplate:
 *   - controlled tour state (useTourController)
 *   - the per-user "seen" flag (tourSeen, keyed by moduleKey)
 *   - auto-launch once for a new user
 *   - registering the opener with the global "Take the Tour" button
 *
 * A module only has to declare what's unique to it (its steps, targets, greeting
 * actions, and any cross-page handoff).
 *
 *   const tour = useModuleTour({ moduleKey: "jobs-library", autoLaunch: !crossPageTourPhase });
 *   <SbTour key={tour.remountKey} open={tour.open} current={tour.current} ... />
 *   // tour.begin / goToStep / close drive it; tour.markSeen() on finish/dismiss.
 *
 * @param {string}  moduleKey        Key under the shared per-user "seen" object.
 * @param {boolean} [autoLaunch]     Auto-open once for a new user (default true).
 *                                   Pass false to suppress (e.g. mid cross-page hop).
 * @param {number}  [autoLaunchDelay] ms to wait before auto-opening (default 500).
 */
export default function useModuleTour({ moduleKey, autoLaunch = true, autoLaunchDelay = 500 }) {
  // Prefer the reactive auth store (source of truth); fall back to a guarded
  // localStorage read like the rest of the app. The token is used only to derive
  // a stable per-user id (survives email changes) that keys the seen flag.
  const tokenFromStore = useAuthStore((s) => s.accessToken);
  const userId = useMemo(
    () => decodeToken(tokenFromStore || readStoredToken())?.sb_user_unique_id ?? null,
    [tokenFromStore],
  );

  const controller = useTourController();
  const { begin } = controller;

  // Zero-arg opener for auto-launch + the global help button (wrapped so a click
  // event never lands in begin's optional start-step arg).
  const startTour = useCallback(() => begin(), [begin]);
  const markSeen = useCallback(() => markTourSeen(userId, moduleKey), [userId, moduleKey]);

  const registerTour = useTourStore((s) => s.registerTour);
  const clearTour = useTourStore((s) => s.clearTour);

  // Auto-launch once for a new user. autoLaunch=false (e.g. a cross-page hop is
  // in flight) marks it done without launching, so it won't fire later either.
  const launchedRef = useRef(false);
  useEffect(() => {
    if (launchedRef.current || !userId) return;
    if (!autoLaunch) {
      launchedRef.current = true;
      return undefined;
    }
    launchedRef.current = true;
    if (isTourSeen(userId, moduleKey)) return undefined;
    const timer = setTimeout(startTour, autoLaunchDelay);
    return () => clearTimeout(timer);
  }, [userId, moduleKey, autoLaunch, autoLaunchDelay, startTour]);

  // Expose the opener to the global "Take the Tour" help button.
  useEffect(() => {
    registerTour(startTour);
    return () => clearTour();
  }, [registerTour, clearTour, startTour]);

  return { ...controller, userId, startTour, markSeen };
}
